import React, { Suspense, useEffect, useMemo, useState } from 'react';

import { type Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { checkVisibility } from 'react-redux-permissions/dist/core';
import { Redirect, Route, Switch, useHistory } from 'react-router';

import { appsActions } from '../../actions/apps.actions';
import { generalConstants } from '../../constants/general.constants';
import { useSearchParams } from '../../custom-hooks/useSearchParams';
import { lazyWithRetry } from '../../helpers/utilities';
import { portalRoutes } from '../../routes/portal.routes';
import { flagService } from '../../services';
import ScrollToTop from './ScrollToTop';
import SideNav from './SideNav';
import TopNav from './TopNav';

const isDev = !window.location?.host?.includes('portal.flipdish.com');
const FlipdishToolbar = lazyWithRetry(
  () => import('../../components/FlipdishToolbar/FlipdishToolbar')
);

const { SCROLLABLE_CONTAINER } = generalConstants;

const tutorialDrawerWidth = 408;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  wrapper: {
    flexGrow: 1,
    width: '100%',
    display: 'flex',
    overflow: 'hidden',
  },
  content: {
    flexGrow: 1,
    width: '100%',
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    overflowX: 'auto',
  },
  switch: {
    zIndex: 1,
    flexGrow: 1,
    display: 'flex',
    overflowY: 'auto',
    overflowX: 'hidden',
    flexDirection: 'column',
    '-webkit-overflow-scrolling': 'touch',
  },
  tutorialDrawerOpen: {
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.up('sm')]: {
      marginRight: tutorialDrawerWidth,
    },
  },
  tutorialDrawerClose: {
    marginRight: 0,
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
}));

const createRoute = (
  props: PortalRoutePropsExtended | PortalRedirectPropsExtended,
  appPermissions: AppState['permissions']
) => {
  if ('to' in props) {
    // only Redirect has `to` prop required
    const { name, key = '', ...rest } = props;
    return <Redirect {...rest} key={`${name}.${key}`} />;
  }

  if ('permissions' in props) {
    const { permissions, name, key = '', ...rest } = props;
    if (checkVisibility(appPermissions, permissions, [])) {
      return <Route {...rest} key={`${name}.${key}`} />;
    }
    return null;
  }

  const { name, key = '', ...rest } = props;
  return <Route {...rest} key={`${name}.${key}`} />;
};

type Props = MappedDispatch & MappedState;

const Portal = (props: Props) => {
  const {
    currentApp,
    disablePropertyLevelAddOns,
    foodSafetyAddOnPermissions,
    getApps,
    isHelpDrawerOpen,
    permissions,
  } = props;

  const history = useHistory();
  const searchParams = useSearchParams();

  const hasIsWebViewParam = (params: URLSearchParams) =>
    [...params.entries()].some(
      ([k, v]) => k.toLowerCase() === 'iswebview' && v.toLowerCase() === 'true'
    );

  const isWebView = hasIsWebViewParam(searchParams);

  useEffect(() => {
    if (!isWebView) return;
    return history.listen((location) => {
      const params = new URLSearchParams(location.search);
      if (!hasIsWebViewParam(params)) {
        params.set('isWebView', 'true');
        history.replace({ ...location, search: `?${params.toString()}` });
      }
    });
  }, [isWebView]);

  // Redirect to onboarding if conditions are met
  //useRedirectToOnboarding();

  const classes = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));
  const [open, setOpen] = useState(false);
  const [openSubDrawer, setOpenSubDrawer] = useState(false);
  const [tutorialIsOpen, setTutorialIsOpen] = useState<boolean | undefined>(undefined);
  const [tutorialHasNotification] = useState<boolean | undefined>(undefined);
  // HIDING Tutorial until work is done to update its api calls etc CLX-1508
  // const tutorialStore = useTutorialStore(currentApp.AppId).data;
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    getApps();
  }, []);

  useEffect(() => {
    setOpen(matches);
  }, [matches]);

  const toggleSideNav = () => {
    if (isMobile) {
      setOpen((prevOpen) => !prevOpen);
    } else if (!openSubDrawer) {
      setOpen((prevOpen) => !prevOpen);
    }
  };

  const drawerOpen = tutorialIsOpen === true || isHelpDrawerOpen === true;
  const drawerClosed = tutorialIsOpen === false && isHelpDrawerOpen === false;

  const routes = useMemo(() => {
    if (!currentApp.AppId) {
      return null;
    }
    return portalRoutes(
      currentApp.AppId,
      disablePropertyLevelAddOns,
      foodSafetyAddOnPermissions
    ).map((r) => createRoute(r, permissions));
  }, [currentApp.AppId, permissions]);

  return (
    <div className={classes.root}>
      {!isWebView && (
        <TopNav
          open={open}
          toggleSideNav={toggleSideNav}
          tutorialIsOpen={tutorialIsOpen}
          setTutorialIsOpen={setTutorialIsOpen}
          // tutorialHasNotification={tutorialHasNotification}
        />
      )}
      <div className={classes.wrapper}>
        {!isWebView && (
          <SideNav
            open={open}
            toggleSideNav={toggleSideNav}
            openSubDrawer={openSubDrawer}
            setOpenSubDrawer={setOpenSubDrawer}
          />
        )}
        <main className={classes.content}>
          <div
            className={clsx({
              [classes.switch]: true,
              [classes.tutorialDrawerOpen]: drawerOpen,
              [classes.tutorialDrawerClose]: drawerClosed,
            })}
            id={SCROLLABLE_CONTAINER}
          >
            <ScrollToTop>
              <Switch>{routes}</Switch>
            </ScrollToTop>
          </div>

          {/* HIDING Tutorial until work is done to update its api calls etc CLX-1508 */}
          {/* {tutorialStore && (
            <TutorialDrawer
              isOpen={tutorialIsOpen}
              setIsOpen={setTutorialIsOpen}
              drawerWidth={tutorialDrawerWidth}
              hasNotification={tutorialHasNotification}
              setHasNotification={setTutorialHasNotification}
              tutorialStore={tutorialStore}
            />
          )} */}
        </main>
      </div>
      {isDev && (
        <Suspense fallback={null}>
          <FlipdishToolbar />
        </Suspense>
      )}
    </div>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
function mapStateToProps(state: AppState) {
  return {
    currentApp: state.currentApp,
    isHelpDrawerOpen: state.drawer.helpDrawerOpen,
    permissions: state.permissions,
    disablePropertyLevelAddOns: flagService.isFlagOn(state, 'disablePropertyManagerLevelAddOns'),
    foodSafetyAddOnPermissions: flagService.isFlagOn(state, 'foodSafetyAddOnPermissions'),
  };
}

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
function mapDispatchToProps(dispatch: ThunkDispatch) {
  return {
    getApps: () => dispatch(appsActions.getApps(undefined, false, undefined, 1, 20)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Portal);
